import { useLayoutEffect } from "react";
import { RefObject } from "use-callback-ref/dist/es5/types";

interface useFocusLockOptions {
	/**
	 * Whether the focus scope is active
	 * Usefull for conditionally rendered UI
	 * @default true
	 */
	isActive?: boolean;

	/**
	 * Whether to auto-focus the scope when activated
	 * @default true
	 */
	autoFocus?: boolean;

	/**
	 * Whether to restore focus when deactivated
	 * @default true
	 */
	restoreFocus?: boolean;
}

function getSelectableSelectors(includeNonTabbableElement = false) {
	const selectors = [
		"button:not([disabled])",
		"[href]",
		"input:not([disabled])",
		"select:not([disabled])",
		"textarea:not([disabled])",
		// tabindex=-1 needed?
		// For user tabs it is needed, for programmatic focus it should not be needed
		'[tabindex]:not([tabindex="-1"]):not([disabled])',
		'[contenteditable="true"]',
	];

	if (includeNonTabbableElement) {
		selectors.push("[tabindex]:not([disabled])");
	} else {
		selectors.push('[tabindex]:not([tabindex="-1"]):not([disabled])');
	}
	return selectors;
}

// Get all focusable elements within the scope
function getFocusableElements(
	el: HTMLElement,
	/**
	 * false: user initiated by pressing Tab
	 * true: programmatic focusable elements (include tabindex=-1)
	 */
	includeNonTabbableElement = false,
): HTMLElement[] {
	const focusableSelector = getSelectableSelectors(includeNonTabbableElement).join(",");
	const elements = Array.from(el.querySelectorAll<HTMLElement>(focusableSelector));

	// Filter out elements not visible
	return elements.filter((element) => {
		const style = window.getComputedStyle(element);
		const rect = element.getBoundingClientRect();

		return (
			style.visibility !== "hidden" &&
			style.display !== "none" &&
			style.opacity !== "0" &&
			rect.width > 0 &&
			rect.height > 0
		);
	});
}

// Focus the first available element in scope
function focusFirstElement(el: HTMLElement | null | undefined) {
	if (!el) return;

	// Prefer user tabbable elements
	const firstElement = getFocusableElements(el, false)[0] ?? getFocusableElements(el, true)[0];

	if (firstElement) {
		firstElement.focus();
	} else {
		// Fallback: focus the scope container itself
		el.focus();
	}
}

// Find the next/previous focusable element
function getNextFocusableElement(
	els: HTMLElement[],
	currentElement: HTMLElement,
	direction: "next" | "prev",
): HTMLElement | null {
	const currentIndex = els.indexOf(currentElement);

	if (direction === "next") {
		return els[currentIndex + 1] || els[0] || null;
	}
	return els[currentIndex - 1] || els[els.length - 1] || null;
}

/**
 * A focus management hook that:
 * 1. Traps Tab/Shift+Tab within the scope
 * 2. Manages focus entry/exit and restoration
 * 3. Handles clicks on non-focusable elements
 * 4. Maintains focus when elements become disabled
 */
export function useFocusLock(
	scopeRef: RefObject<HTMLElement | null | undefined>,
	options: useFocusLockOptions = {},
) {
	const { isActive = true, autoFocus = true, restoreFocus = true } = options;

	useFocusRing(scopeRef, {
		isActive,
	});

	useFocusInside(scopeRef, {
		isActive,
	});

	// Move focus to the first element in the scope
	useFocusMove(scopeRef, {
		isActive,
		autoFocus,
		restoreFocus,
	});
}

/**
 * Stack to allow nested focus rings
 * Place in  react context, valtio, ...?
 */
const focusRingsStack: HTMLElement[] = [];

function useFocusRing(
	scopeRef: RefObject<HTMLElement | null | undefined>,
	{ isActive = true }: { isActive?: boolean } = {},
) {
	// Handle Tab keyboard navigation
	useLayoutEffect(() => {
		if (!isActive || !scopeRef.current) return undefined;

		const scopeEl = scopeRef.current;

		const handleKeyDown = (event: KeyboardEvent) => {
			// Only handle Tab and Shift+Tab
			if (event.key !== "Tab") return;

			// Prevent conflicting Focus Rings, last one wins
			// Example: if a popup opens op popup
			if (scopeEl !== focusRingsStack.at(-1)) return;

			const focusableElements = getFocusableElements(scopeEl, false);
			const activeElement = document.activeElement as HTMLElement;

			// If no focusable elements, prevent default and focus container
			if (focusableElements.length === 0) {
				event.preventDefault();
				scopeEl.focus();
				return;
			}

			// If focus is outside scope, bring it back
			if (!scopeEl.contains(activeElement)) {
				event.preventDefault();
				focusableElements[0].focus();
				return;
			}

			// Handle tab navigation within scope
			const direction = event.shiftKey ? "prev" : "next";
			const nextElement = getNextFocusableElement(
				focusableElements,
				activeElement,
				direction,
			);

			if (nextElement) {
				event.preventDefault();
				nextElement.focus();
			}
		};

		scopeEl.addEventListener("keydown", handleKeyDown);
		focusRingsStack.push(scopeEl);

		return () => {
			scopeEl.removeEventListener("keydown", handleKeyDown);
			const removedIndex = focusRingsStack.lastIndexOf(scopeEl);
			if (removedIndex >= 0) focusRingsStack.splice(removedIndex, 1);
		};
	}, [isActive, scopeRef]);
}

function useFocusInside(
	scopeRef: RefObject<HTMLElement | null | undefined>,
	{ isActive = true }: { isActive?: boolean } = {},
) {
	// Handle clicks on non-focusable elements
	useLayoutEffect(() => {
		if (!isActive || !scopeRef.current) return undefined;

		const scopeEl = scopeRef.current;

		const handleClick = (event: MouseEvent) => {
			const target = event.target as HTMLElement;

			// If we click a child element, do nothing, if focusable element clicked it will stay in scope
			// This was to prevent a scroll when clicking a non-focusable element inside the scope
			if (scopeEl.contains(target)) return;

			// If clicked element is not focusable, focus the container or first focusable element
			if (!target.matches(getSelectableSelectors(true).join(","))) {
				// Small delay to ensure click event is processed
				setTimeout(() => {
					focusFirstElement(scopeEl);
				}, 0);
			}
		};

		scopeEl.addEventListener("click", handleClick);

		return (): void => {
			scopeEl.removeEventListener("click", handleClick);
		};
	}, [isActive, scopeRef]);

	// Handle focus loss (example: when elements become disabled)
	useLayoutEffect(() => {
		if (!isActive || !scopeRef.current) return undefined;

		const scopeEl = scopeRef.current;

		const handleFocusOut = (_event: FocusEvent) => {
			// Use setTimeout to handle async changes (like elements becoming disabled)
			setTimeout(() => {
				const activeElement = document.activeElement;

				// If focus moved outside the scope or to body, bring it back
				if (
					activeElement === document.body ||
					(activeElement && !scopeEl.contains(activeElement))
				) {
					focusFirstElement(scopeEl);
				}
			}, 0);
		};

		scopeEl.addEventListener("focusout", handleFocusOut);

		return (): void => {
			scopeEl.removeEventListener("focusout", handleFocusOut);
		};
	}, [isActive, scopeRef]);
}

/** Move focus to scope element and restores on unmount */
export function useFocusMove(
	scopeRef: RefObject<HTMLElement | null | undefined>,
	{
		isActive = true,
		autoFocus = true,
		restoreFocus = true,
	}: {
		/**
		 * Whether the focus scope is active
		 * Usefull for conditionally rendered UI
		 * @default true
		 */
		isActive?: boolean;

		/**
		 * Whether to auto-focus the scope when activated
		 * @default true
		 */
		autoFocus?: boolean;

		/**
		 * Whether to restore focus when deactivated
		 * @default true
		 */
		restoreFocus?: boolean;
	} = {},
) {
	// Initial focus and restoration
	useLayoutEffect(() => {
		if (!isActive) return undefined;

		// Store the currently focused element
		const previousFocusedEl = document.activeElement as HTMLElement;

		// Auto-focus if requested
		if (autoFocus) {
			// Small delay to ensure the scope is fully rendered
			setTimeout(() => {
				focusFirstElement(scopeRef.current);
			}, 0);
		}

		// Cleanup: restore focus when scope becomes inactive
		return () => {
			if (restoreFocus && previousFocusedEl) {
				// Check if the previous element still exists and is focusable
				if (document.contains(previousFocusedEl)) {
					try {
						previousFocusedEl.focus();
					} catch {
						// Element might not be focusable anymore, ignore
					}
				}
			}
		};
	}, [isActive, autoFocus, restoreFocus, scopeRef]);
}
